import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';

import { AppState } from '../../redux';
import { IOrderProduct } from '../../types/orderProduct';
import AddressForm from '../../components/checkout/AddressForm';
import Review from '../../components/checkout/Review';
import { IOrder, IUser } from '../../types/strapi';
import { axiosClient } from '../../helpers/AxiosClient';
import { emptyCartAction } from '../../redux/actions';
import { getAuthenticationState } from '../../redux/selectors';
import { Delivery } from '../../components/checkout/Delivery';
import { calcOrderProductsSubtotal } from '../../helpers/utils';
import { useNavigate } from 'react-router-dom';
import { useOnlineSalesConfig } from '../../hooks';

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
  },
  layout: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      width: 600,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  error: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px 0',
    color: theme.palette.error.main,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
}));

const steps = ['Indirizzo di consegna', 'Data di consegna', "Rivedi l'ordine"];

interface CheckoutProps {
  user: IUser;
  items: IOrderProduct[];
  emptyCartAction: typeof emptyCartAction;
}

const Checkout: FC<CheckoutProps> = ({ user, items, emptyCartAction }) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = React.useState(0);
  const [requesting, setRequesting] = useState(false);
  const theme = useTheme();
  const mediaQueryMatchesXs = useMediaQuery(theme.breakpoints.down('xs'));
  const { config } = useOnlineSalesConfig();

  const [order, setOrder] = useState<Partial<IOrder>>({});
  const [rRuleByDay, setRRuleByDay] = useState('');

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      const { shippingCosts } = calcOrderProductsSubtotal(items, !!user.partner, config);
      const { orders, ...cleanUser } = user;
      (cleanUser as IUser).orders = [];
      setOrder({
        user: cleanUser as IUser,
        name: user?.name,
        surname: user?.surname,
        productsWithQuantity: items,
        shippingCosts,
      });
    }
  }, [items, user, config]);

  const _renderActiveStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <AddressForm
            defaultValues={{
              name: order.name || user?.name || '',
              surname: order.surname || user?.surname || '',
            }}
            partner={user?.partner}
            onChange={(addressInfoChanges) => {
              const { rRuleByDay, ...otherAddressInfoChanges } = addressInfoChanges;
              setOrder({ ...order, ...otherAddressInfoChanges });
              setRRuleByDay(rRuleByDay);
            }}
          />
        );
      case 1:
        return <Delivery rRuleByDay={rRuleByDay} onChange={(deliveryDate) => setOrder({ ...order, deliveryDate })} />;
      case 2:
        return <Review user={user} items={items} order={order} />;
    }
  };

  const handleNext = () => {
    setError(null);
    if (activeStep === 0) {
      if (
        order &&
        order.name !== '' &&
        order.surname !== '' &&
        (order.address ?? '') !== '' &&
        (order.city ?? '') !== ''
      ) {
        setActiveStep(1);
      } else {
        setError('È necessario completare tutti i campi');
      }
    } else if (activeStep === 1) {
      if (rRuleByDay === '' || (rRuleByDay !== '' && !!order.deliveryDate)) {
        setActiveStep(2);
      } else {
        setError('È necessario completare tutti i campi');
      }
    } else {
      setRequesting(true);
      axiosClient
        .post('orders', { data: { ...order, user: { connect: [user.id] } } })
        .then(({ data: order }) => {
          emptyCartAction();
          axiosClient
            .get(`orders/${order.id}/notify`)
            .then(() => {
              setRequesting(false);
              navigate(`/confirmation?order_id=${order.id}`);
            })
            .catch(() => {
              setRequesting(false);
              navigate(`/confirmation?order_id=${order.id}`);
            });
        })
        .catch((reason) => {
          setRequesting(false);
          enqueueSnackbar("Impossibile completare l'ordine", { variant: 'error' });
          console.log(reason);
        });
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  return (
    <Container>
      <Paper className={classes.paper}>
        <Typography variant="h3" gutterBottom color="primary">
          Checkout
        </Typography>
        <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel={mediaQueryMatchesXs}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {_renderActiveStep()}
        <div className={classes.error}>{error}</div>
        <div className={classes.buttons}>
          {activeStep !== 0 && (
            <Button onClick={handleBack} className={classes.button}>
              Indietro
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={requesting}
            className={classes.button}
          >
            {activeStep === steps.length - 1 ? 'Concludi' : 'Avanti'}
          </Button>
        </div>
      </Paper>
    </Container>
  );
};

const mapStateToProps = (state: AppState) => ({
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  user: getAuthenticationState(state).user!,
  items: state.cart.items,
});

export default connect(mapStateToProps, { emptyCartAction })(Checkout);
